import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Sparkles, RefreshCw, CheckCircle, AlertCircle } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface MissingQuizModule {
  course_id: string;
  course_title: string;
  module_id: string;
  module_title: string;
  content_word_count: number;
  estimated_duration: number;
  has_content: boolean;
}

export default function QuizGeneration() {
  const [modules, setModules] = useState<MissingQuizModule[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentModule, setCurrentModule] = useState('');

  const fetchMissingQuizzes = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.rpc('get_modules_missing_quizzes', { limit_count: 500 });
      
      if (error) throw error;
      
      setModules(data || []);
    } catch (error) {
      console.error('Error fetching missing quizzes:', error);
      toast.error('Failed to fetch missing quizzes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMissingQuizzes();
  }, []);

  const generateAllQuizzes = async () => {
    if (!modules.length) return;

    setGenerating(true);
    let successCount = 0;
    let failureCount = 0;

    for (let i = 0; i < modules.length; i++) {
      const module = modules[i];
      setCurrentModule(module.module_title);
      setProgress(((i + 1) / modules.length) * 100);

      try {
        const { error } = await supabase.functions.invoke('generate-quiz', {
          body: {
            module_id: module.module_id,
            course_id: module.course_id
          }
        });

        if (error) throw error;
        successCount++;
        
        // Small delay to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 2000));
      } catch (error) {
        console.error(`Failed to generate quiz for ${module.module_id}:`, error);
        failureCount++;
      }
    }

    setGenerating(false);
    setProgress(0);
    setCurrentModule('');

    toast.success(`Quiz generation complete! ${successCount} successful, ${failureCount} failed`);
    fetchMissingQuizzes(); // Refresh the list
  };

  const generateSingleQuiz = async (moduleId: string, courseId: string) => {
    try {
      const { error } = await supabase.functions.invoke('generate-quiz', {
        body: { module_id: moduleId, course_id: courseId }
      });

      if (error) throw error;

      toast.success('Quiz generated successfully!');
      fetchMissingQuizzes();
    } catch (error) {
      console.error('Error generating quiz:', error);
      toast.error('Failed to generate quiz');
    }
  };

  const groupedByCourse = modules.reduce((acc, module) => {
    if (!acc[module.course_id]) {
      acc[module.course_id] = {
        title: module.course_title,
        modules: []
      };
    }
    acc[module.course_id].modules.push(module);
    return acc;
  }, {} as Record<string, { title: string; modules: MissingQuizModule[] }>);

  return (
    <div className="container mx-auto py-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Universal Quiz Generator</h1>
          <p className="text-muted-foreground mt-1">
            Generate quizzes for all modules with content but no quizzes
          </p>
        </div>
        <Button onClick={fetchMissingQuizzes} disabled={loading} variant="outline">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Card */}
      <Card className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">{modules.length} Modules Missing Quizzes</h2>
            <p className="text-muted-foreground mt-1">
              Across {Object.keys(groupedByCourse).length} courses
            </p>
          </div>
          <Button 
            onClick={generateAllQuizzes} 
            disabled={generating || modules.length === 0}
            size="lg"
          >
            <Sparkles className="h-5 w-5 mr-2" />
            Generate All Quizzes
          </Button>
        </div>

        {generating && (
          <div className="mt-6 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span>Generating: {currentModule}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} />
          </div>
        )}
      </Card>

      {/* Modules by Course */}
      <div className="space-y-6">
        {Object.entries(groupedByCourse).map(([courseId, { title, modules: courseModules }]) => (
          <Card key={courseId} className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-xl font-semibold">{title}</h3>
                <p className="text-sm text-muted-foreground">
                  {courseModules.length} modules need quizzes
                </p>
              </div>
              <Badge variant="outline">
                {courseModules.length} missing
              </Badge>
            </div>

            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Module Title</TableHead>
                    <TableHead className="text-center">Word Count</TableHead>
                    <TableHead className="text-center">Duration (min)</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {courseModules.map((module) => (
                    <TableRow key={module.module_id}>
                      <TableCell className="font-medium">
                        {module.module_title}
                      </TableCell>
                      <TableCell className="text-center">
                        {module.content_word_count.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-center">
                        {module.estimated_duration || 'N/A'}
                      </TableCell>
                      <TableCell className="text-center">
                        {module.has_content ? (
                          <Badge variant="outline" className="bg-green-50 text-green-700">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Has Content
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-red-50 text-red-700">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            No Content
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => generateSingleQuiz(module.module_id, module.course_id)}
                          disabled={generating || !module.has_content}
                        >
                          <Sparkles className="h-3 w-3 mr-1" />
                          Generate
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        ))}
      </div>

      {modules.length === 0 && !loading && (
        <Card className="p-12 text-center">
          <CheckCircle className="h-16 w-16 mx-auto text-green-500 mb-4" />
          <h3 className="text-2xl font-semibold mb-2">All Quizzes Generated!</h3>
          <p className="text-muted-foreground">
            Every module with content now has a quiz. Great work! 🎉
          </p>
        </Card>
      )}
    </div>
  );
}
